import { ImageUploadField, ImageUploadTarget } from './ImageUploadField';
import { useEffect, useRef, useState } from 'react';
import { Modal } from './Modal';
import styled from 'styled-components';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Circle,
  Autocomplete,
  Libraries,
} from '@react-google-maps/api';

const GOOGLE_MAPS_LIBRARIES: Libraries = ['places'];

const AWARDING_RADIUS_COLOR = '#4CAF50';
const CLOSE_RADIUS_COLOR = '#FF9800';

export type OptionEntryForm = {
  name: string;
  value: number;
  options: string[];
};

export type FreeEntryForm = {
  name: string;
  value: string;
  characterLimit: number;
  multiline?: boolean;
  helpText?: string;
};

export type NumberEntryForm = {
  name: string;
  value: number;
  min: number;
  max: number;
};

export type MapEntryForm = {
  name: string;
  latitude: number;
  longitude: number;
  awardingRadiusF?: number;
  closeRadiusF?: number;
};

export type DateEntryForm = {
  name: string;
  date: Date;
};

export type CheckboxNumberEntryForm = {
  name: string;
  checked: boolean;
  value: number;
  min: number;
  max: number;
  numberLabel: string;
};

export type AnswersEntryForm = {
  name: string;
  answers: Array<{ text: string; isCorrect: boolean }>;
  minAnswers: number;
  maxAnswers: number;
};

export type CheckboxDateEntryForm = {
  name: string;
  checked: boolean;
  date: Date;
};

export type OptionWithCustomEntryForm = {
  name: string;
  value: number;
  options: string[];
  customValue: string;
  customOptionLabel: string;
};

export type EntryForm =
  | OptionEntryForm
  | FreeEntryForm
  | NumberEntryForm
  | MapEntryForm
  | DateEntryForm
  | CheckboxNumberEntryForm
  | AnswersEntryForm
  | OptionWithCustomEntryForm
  | CheckboxDateEntryForm;

const EntryBox = styled.div`
  margin-bottom: 12px;
  display: flex;
  flex-direction: row;
`;

const EntryTextBox = styled.input`
  font-size: 18px;
  margin-left: 12px;
  border-width: 1px;
  height: 28px;
  border-radius: 2px;
  flex-grow: 1;
`;

const EntryTextArea = styled.textarea`
  font-size: 18px;
  margin-left: 12px;
  border-width: 1px;
  border-radius: 2px;
  flex-grow: 1;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
`;

const EntrySelect = styled.select`
  margin-left: 12px;
  border-width: 1px;
  height: 24px;
  border-radius: 2px;
`;

function OptionEntryFormBox(props: { form: OptionEntryForm }) {
  const [val, setVal] = useState(props.form.options[props.form.value]);

  useEffect(() => setVal(props.form.options[props.form.value]), [props.form]);

  return (
    <EntryBox>
      <label htmlFor={props.form.name}>{props.form.name + ':'}</label>
      <EntrySelect
        name={props.form.name}
        value={val}
        onChange={e =>
          setVal(
            props.form.options[(props.form.value = e.target.selectedIndex)],
          )
        }
      >
        {props.form.options.map(val => (
          <option key={val} onSelect={() => console.log(val)}>
            {val}
          </option>
        ))}
      </EntrySelect>
    </EntryBox>
  );
}

const HelpText = styled.div`
  font-size: 12px;
  color: #666;
  margin-left: 12px;
  margin-top: 4px;
  font-style: italic;
`;

function FreeEntryFormBox(props: { form: FreeEntryForm }) {
  const [val, setVal] = useState('');

  useEffect(() => setVal(props.form.value), [props.form]);

  return (
    <>
      <EntryBox>
        <span>{props.form.name + ':'}</span>
        {props.form.multiline ? (
          <EntryTextArea
            value={val}
            maxLength={props.form.characterLimit}
            onChange={e => setVal((props.form.value = e.target.value))}
          />
        ) : (
          <EntryTextBox
            value={val}
            maxLength={props.form.characterLimit}
            onChange={e => setVal((props.form.value = e.target.value))}
          />
        )}
      </EntryBox>
      {props.form.helpText && <HelpText>{props.form.helpText}</HelpText>}
    </>
  );
}

function DateEntryFormBox(props: { form: DateEntryForm }) {
  const [val, setVal] = useState('');

  useEffect(
    () => setVal(props.form.date.toISOString().slice(0, -1)),
    [props.form],
  );

  return (
    <EntryBox>
      <span>{props.form.name + ':'}</span>
      <EntryTextBox
        type="datetime-local"
        value={val}
        onChange={e => {
          setVal(e.target.value);
          props.form.date = new Date(e.target.value);
        }}
      />
    </EntryBox>
  );
}

function NumberEntryFormBox(props: {
  form: NumberEntryForm;
  onChange?: (value: number) => void;
}) {
  const [val, setVal] = useState('');

  useEffect(() => setVal('' + props.form.value), [props.form]);

  return (
    <EntryBox>
      {props.form.name + ': '}
      <EntryTextBox
        type="number"
        value={val}
        min={props.form.min}
        max={props.form.max}
        onChange={e => {
          const num = +e.target.value;
          setVal(e.target.value);
          props.form.value = num;
          props.onChange?.(num);
        }}
      />
    </EntryBox>
  );
}

function CheckboxNumberEntryFormBox(props: { form: CheckboxNumberEntryForm }) {
  const [checked, setChecked] = useState(props.form.checked);
  const [val, setVal] = useState('' + props.form.value);

  useEffect(() => {
    setChecked(props.form.checked);
    setVal('' + props.form.value);
  }, [props.form]);

  return (
    <>
      <EntryBox>
        <label>
          <input
            type="checkbox"
            checked={checked}
            onChange={e => {
              setChecked(e.target.checked);
              props.form.checked = e.target.checked;
            }}
          />
          {' ' + props.form.name}
        </label>
      </EntryBox>
      {checked && (
        <EntryBox>
          {props.form.numberLabel + ': '}
          <EntryTextBox
            type="number"
            value={val}
            min={props.form.min}
            max={props.form.max}
            onChange={e => {
              setVal(e.target.value);
              props.form.value = +e.target.value;
            }}
          />
        </EntryBox>
      )}
    </>
  );
}

function CheckboxDateEntryFormBox(props: { form: CheckboxDateEntryForm }) {
  const [checked, setChecked] = useState(props.form.checked);
  const [val, setVal] = useState('');

  useEffect(() => {
    setChecked(props.form.checked);
    setVal(props.form.date.toISOString().slice(0, 16));
  }, [props.form]);

  return (
    <>
      <EntryBox>
        <label>
          <input
            type="checkbox"
            checked={checked}
            onChange={e => {
              setChecked(e.target.checked);
              props.form.checked = e.target.checked;
            }}
          />
          {' ' + props.form.name}
        </label>
      </EntryBox>
      {checked && (
        <EntryBox>
          <EntryTextBox
            type="datetime-local"
            value={val}
            onChange={e => {
              setVal(e.target.value);
              props.form.date = new Date(e.target.value);
            }}
          />
        </EntryBox>
      )}
    </>
  );
}

const MapBox = styled.div`
  width: 100%;
  height: 300px;
  margin-bottom: 32px;
  overflow: hidden;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const mapContainerStyle = {
  width: '100%',
  height: '300px',
};

type FrontendConfigResponse = {
  googleMapsApiKey?: string;
};

function MapEntryFormBox(props: {
  form: MapEntryForm;
  allForms?: EntryForm[];
}) {
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState(
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  );
  const [hasLoadedConfig, setHasLoadedConfig] = useState(
    Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY),
  );

  useEffect(() => {
    if (googleMapsApiKey) {
      setHasLoadedConfig(true);
      return;
    }

    let isCancelled = false;

    fetch('/frontend-config')
      .then(async response => {
        if (!response.ok) {
          throw new Error(`Failed to load config: ${response.status}`);
        }

        const config = (await response.json()) as FrontendConfigResponse;

        if (isCancelled) {
          return;
        }

        setGoogleMapsApiKey(config.googleMapsApiKey || '');
        setHasLoadedConfig(true);
      })
      .catch(() => {
        if (!isCancelled) {
          setHasLoadedConfig(true);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [googleMapsApiKey]);

  if (!hasLoadedConfig) {
    return <MapBox>Loading map configuration...</MapBox>;
  }

  if (!googleMapsApiKey) {
    return (
      <MapBox>
        Google Maps is unavailable. Set `REACT_APP_GOOGLE_MAPS_API_KEY` in the
        running server environment.
      </MapBox>
    );
  }

  return (
    <LoadedMapEntryFormBox
      form={props.form}
      allForms={props.allForms}
      googleMapsApiKey={googleMapsApiKey}
    />
  );
}

function LoadedMapEntryFormBox(props: {
  form: MapEntryForm;
  allForms?: EntryForm[];
  googleMapsApiKey: string;
}) {
  const [lat, setLat] = useState(props.form.latitude);
  const [lng, setLng] = useState(props.form.longitude);
  const [markerPosition, setMarkerPosition] =
    useState<google.maps.LatLngLiteral>({
      lat: props.form.latitude,
      lng: props.form.longitude,
    });

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: props.googleMapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  useEffect(() => {
    const styleId = 'pac-container-zindex-fix';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `.pac-container { z-index: 10000 !important; }`;
      document.head.appendChild(style);
    }
    return () => {
      document.querySelectorAll('.pac-container').forEach(el => el.remove());
    };
  }, []);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (place?.geometry?.location) {
      const newLat = place.geometry.location.lat();
      const newLng = place.geometry.location.lng();
      setLat(newLat);
      setLng(newLng);
      props.form.latitude = newLat;
      props.form.longitude = newLng;
      setMarkerPosition({ lat: newLat, lng: newLng });
    }
  };

  // Derive radii from form so map updates when Awarding/Close Distance fields change
  const awardingRadius =
    (
      props.allForms?.find(
        f => 'name' in f && f.name === 'Awarding Distance (meters)',
      ) as NumberEntryForm | undefined
    )?.value ??
    props.form.awardingRadiusF ??
    0;
  const closeRadius =
    (
      props.allForms?.find(
        f => 'name' in f && f.name === 'Close Distance (meters)',
      ) as NumberEntryForm | undefined
    )?.value ??
    props.form.closeRadiusF ??
    0;

  useEffect(() => {
    setLat(props.form.latitude);
    setLng(props.form.longitude);
    setMarkerPosition({
      lat: props.form.latitude,
      lng: props.form.longitude,
    });
  }, [props.form.latitude, props.form.longitude]);

  const handleLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLat = parseFloat(e.target.value);
    if (!isNaN(newLat)) {
      setLat(newLat);
      props.form.latitude = newLat;
      setMarkerPosition({ lat: newLat, lng });
    }
  };

  const handleLngChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLng = parseFloat(e.target.value);
    if (!isNaN(newLng)) {
      setLng(newLng);
      props.form.longitude = newLng;
      setMarkerPosition({ lat, lng: newLng });
    }
  };

  const onMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const newLat = e.latLng.lat();
      const newLng = e.latLng.lng();
      setLat(newLat);
      setLng(newLng);
      props.form.latitude = newLat;
      props.form.longitude = newLng;
      setMarkerPosition({ lat: newLat, lng: newLng });
    }
  };

  if (loadError) {
    return (
      <MapBox>
        Unable to load Google Maps. Verify the API key, referrer restrictions,
        and that the Maps JavaScript API is enabled.
      </MapBox>
    );
  }

  if (!isLoaded) return <MapBox>Loading map...</MapBox>;

  return (
    <>
      <EntryBox>
        <span>Search:</span>
        <Autocomplete
          onLoad={ac => {
            autocompleteRef.current = ac;
          }}
          onPlaceChanged={onPlaceChanged}
        >
          <EntryTextBox
            type="text"
            placeholder="Search for a place (e.g. Cornell Clock Tower)"
          />
        </Autocomplete>
      </EntryBox>
      <MapBox>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={markerPosition}
          zoom={20}
          options={{
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: false,
          }}
        >
          <Marker
            position={markerPosition}
            draggable={true}
            onDragEnd={onMarkerDragEnd}
            title="Drag the pin to move the location"
          />
          {awardingRadius > 0 && (
            <Circle
              center={markerPosition}
              radius={awardingRadius}
              options={{
                fillColor: AWARDING_RADIUS_COLOR,
                fillOpacity: 0.2,
                strokeColor: AWARDING_RADIUS_COLOR,
                strokeOpacity: 0.8,
                strokeWeight: 2,
              }}
            />
          )}
          {closeRadius > 0 && (
            <Circle
              center={markerPosition}
              radius={closeRadius}
              options={{
                fillColor: CLOSE_RADIUS_COLOR,
                fillOpacity: 0.2,
                strokeColor: CLOSE_RADIUS_COLOR,
                strokeOpacity: 0.8,
                strokeWeight: 2,
              }}
            />
          )}
        </GoogleMap>
      </MapBox>
      <EntryBox>
        <span>Latitude:</span>
        <EntryTextBox type="number" value={lat} onChange={handleLatChange} />
      </EntryBox>
      <EntryBox>
        <span>Longitude:</span>
        <EntryTextBox type="number" value={lng} onChange={handleLngChange} />
      </EntryBox>
    </>
  );
}

// Styled components for AnswersEntryForm
const AnswerRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
`;

const AnswerInput = styled.input`
  flex: 1;
  font-size: 16px;
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const CorrectRadio = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const AnswerButton = styled.button`
  padding: 4px 12px;
  cursor: pointer;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #f5f5f5;
  &:hover {
    background: #e0e0e0;
  }
`;

const AnswersContainer = styled.div`
  margin-bottom: 16px;
`;

function AnswersEntryFormBox(props: { form: AnswersEntryForm }) {
  const [answers, setAnswers] = useState(props.form.answers);

  useEffect(() => {
    setAnswers([...props.form.answers]);
  }, [props.form]);

  const updateAnswerText = (index: number, text: string) => {
    const newAnswers = [...answers];
    newAnswers[index].text = text;
    setAnswers(newAnswers);
    props.form.answers = newAnswers;
  };

  const toggleCorrect = (index: number) => {
    const newAnswers = answers.map((a, i) => ({
      ...a,
      isCorrect: i === index,
    }));
    setAnswers(newAnswers);
    props.form.answers = newAnswers;
  };

  const addAnswer = () => {
    if (answers.length < props.form.maxAnswers) {
      const newAnswers = [...answers, { text: '', isCorrect: false }];
      setAnswers(newAnswers);
      props.form.answers = newAnswers;
    }
  };

  const removeAnswer = (index: number) => {
    if (answers.length > props.form.minAnswers) {
      const newAnswers = answers.filter((_, i) => i !== index);
      if (!newAnswers.some(a => a.isCorrect) && newAnswers.length > 0) {
        newAnswers[0].isCorrect = true;
      }
      setAnswers(newAnswers);
      props.form.answers = newAnswers;
    }
  };

  return (
    <AnswersContainer>
      <div style={{ marginBottom: 8 }}>{props.form.name}:</div>
      {answers.map((answer, index) => (
        <AnswerRow key={index}>
          <CorrectRadio
            type="radio"
            name="correctAnswer"
            checked={answer.isCorrect}
            onChange={() => toggleCorrect(index)}
            title="Mark as correct"
          />
          <AnswerInput
            value={answer.text}
            placeholder={`Answer ${index + 1}`}
            onChange={e => updateAnswerText(index, e.target.value)}
          />
          {answers.length > props.form.minAnswers && (
            <AnswerButton onClick={() => removeAnswer(index)}>-</AnswerButton>
          )}
        </AnswerRow>
      ))}
      {answers.length < props.form.maxAnswers && (
        <AnswerButton onClick={addAnswer}>+ Add Answer</AnswerButton>
      )}
    </AnswersContainer>
  );
}

function OptionWithCustomEntryFormBox(props: {
  form: OptionWithCustomEntryForm;
}) {
  const [selectedIndex, setSelectedIndex] = useState(props.form.value);
  const [customText, setCustomText] = useState(props.form.customValue);

  const isCustomSelected = selectedIndex === props.form.options.length;

  useEffect(() => {
    setSelectedIndex(props.form.value);
    setCustomText(props.form.customValue);
  }, [props.form]);

  return (
    <>
      <EntryBox>
        <label htmlFor={props.form.name}>{props.form.name + ':'}</label>
        <EntrySelect
          name={props.form.name}
          value={
            isCustomSelected
              ? props.form.customOptionLabel
              : props.form.options[selectedIndex]
          }
          onChange={e => {
            const idx = e.target.selectedIndex;
            setSelectedIndex(idx);
            props.form.value = idx;
          }}
        >
          {props.form.options.map(opt => (
            <option key={opt}>{opt}</option>
          ))}
          <option>{props.form.customOptionLabel}</option>
        </EntrySelect>
      </EntryBox>
      {isCustomSelected && (
        <EntryBox>
          <span>Custom value:</span>
          <EntryTextBox
            value={customText}
            placeholder="Enter custom category"
            onChange={e => {
              setCustomText(e.target.value);
              props.form.customValue = e.target.value;
            }}
          />
        </EntryBox>
      )}
    </>
  );
}

export function EntryModal(props: {
  title: string;
  form: EntryForm[];
  isOpen: boolean;
  entryButtonText: string;
  imageUploadTarget?: ImageUploadTarget;
  onEntry: () => void;
  onCancel: () => void;
  /** When provided, radius number changes update parent state so the map re-renders. */
  onRadiusChange?: (awardingRadius: number, closeRadius: number) => void;
}) {
  const [imageUploading, setImageUploading] = useState(false);
  useEffect(() => {
    if (!props.isOpen) setImageUploading(false);
  }, [props.isOpen]);
  const getAwarding = () =>
    (
      props.form.find(
        f => 'name' in f && f.name === 'Awarding Distance (meters)',
      ) as NumberEntryForm | undefined
    )?.value ?? 0;
  const getClose = () =>
    (
      props.form.find(
        f => 'name' in f && f.name === 'Close Distance (meters)',
      ) as NumberEntryForm | undefined
    )?.value ?? 0;

  return (
    <Modal
      title={props.title}
      buttons={[props.entryButtonText, 'CANCEL']}
      disabledButtons={imageUploading ? [0] : []}
      isOpen={props.isOpen}
      onButtonClick={idx => {
        if (idx === 1) props.onCancel();
        else if (!imageUploading) props.onEntry();
      }}
    >
      {props.form.map(form => {
        if (
          form.name === 'Image URL' &&
          'characterLimit' in form &&
          props.imageUploadTarget
        ) {
          return (
            <ImageUploadField
              key={form.name}
              form={form}
              target={props.imageUploadTarget}
              onBusyChange={setImageUploading}
            />
          );
        } else if ('answers' in form) {
          return <AnswersEntryFormBox form={form} key={form.name} />;
        } else if ('customOptionLabel' in form) {
          return <OptionWithCustomEntryFormBox form={form} key={form.name} />;
        } else if ('options' in form) {
          return <OptionEntryFormBox form={form} key={form.name} />;
        } else if ('characterLimit' in form) {
          return <FreeEntryFormBox form={form} key={form.name} />;
        } else if ('date' in form && 'checked' in form) {
          return <CheckboxDateEntryFormBox form={form} key={form.name} />;
        } else if ('checked' in form) {
          return <CheckboxNumberEntryFormBox form={form} key={form.name} />;
        } else if ('min' in form) {
          const isAwarding = form.name === 'Awarding Distance (meters)';
          const isClose = form.name === 'Close Distance (meters)';
          const onRadiusChange = props.onRadiusChange;
          return (
            <NumberEntryFormBox
              form={form}
              key={form.name}
              onChange={
                (isAwarding || isClose) && onRadiusChange
                  ? value =>
                      onRadiusChange(
                        isAwarding ? value : getAwarding(),
                        isClose ? value : getClose(),
                      )
                  : undefined
              }
            />
          );
        } else if ('date' in form) {
          return <DateEntryFormBox form={form} key={form.name} />;
        } else {
          return (
            <MapEntryFormBox
              form={form}
              allForms={props.form}
              key={form.name}
            />
          );
        }
      })}
    </Modal>
  );
}
